package com.example.event_management_system.ui.screens.events

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.TextButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Snackbar
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.ui.components.common.ConfirmDialog
import com.example.event_management_system.ui.components.common.ErrorMessage
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.components.event.EventStatusBadge
import com.example.event_management_system.ui.theme.Dimens
import com.example.event_management_system.util.printEventConfirmation
import event_management_system.composeapp.generated.resources.Res
import event_management_system.composeapp.generated.resources.*
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

data class EventDetailScreen(val eventId: String) : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = koinScreenModel<EventDetailScreenModel>()
        val state by screenModel.state.collectAsState()
        var showStatusMenu by remember { mutableStateOf(false) }
        var pendingStatus by remember { mutableStateOf<EventStatus?>(null) }
        val snackbarHostState = remember { SnackbarHostState() }

        LaunchedEffect(eventId) {
            screenModel.loadEvent(eventId)
        }

        LaunchedEffect(state.isDeleted) {
            if (state.isDeleted) {
                navigator.pop()
            }
        }

        LaunchedEffect(state.emailSentSuccess) {
            if (state.emailSentSuccess) {
                snackbarHostState.showSnackbar(
                    message = "Email sent successfully!",
                    duration = SnackbarDuration.Short
                )
                navigator.pop()
            }
        }

        Scaffold(
            snackbarHost = {
                SnackbarHost(hostState = snackbarHostState) { data ->
                    Snackbar(
                        snackbarData = data,
                        containerColor = Color(0xFF16A34A),
                        contentColor = Color.White
                    )
                }
            },
            topBar = {
                TopAppBar(
                    title = { Text(stringResource(Res.string.screen_title_event_details)) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    },
                    actions = {
                        TextButton(
                            onClick = { navigator.push(EventFormScreen(eventId = eventId)) }
                        ) {
                            Text(stringResource(Res.string.action_edit))
                        }
                        if (screenModel.canDelete()) {
                            TextButton(onClick = screenModel::showDeleteConfirmation) {
                                Text(
                                    stringResource(Res.string.action_delete),
                                    color = MaterialTheme.colorScheme.error
                                )
                            }
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        titleContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            }
        ) { padding ->
            when {
                state.isLoading || state.isDeleting -> {
                    LoadingIndicator()
                }
                state.error != null -> {
                    ErrorMessage(
                        message = state.error!!,
                        onRetry = screenModel::refresh
                    )
                }
                state.event != null -> {
                    val event = state.event!!
                    
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(padding)
                            .verticalScroll(rememberScrollState())
                            .padding(Dimens.paddingMedium)
                    ) {
                        // Header with status badge
                        Row(modifier = Modifier.fillMaxWidth()) {
                            EventStatusBadge(status = event.status, large = true)
                        }

                        Spacer(modifier = Modifier.height(Dimens.spacingMedium))

                        // Customer Contact Card
                        OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
                                Text(
                                    text = stringResource(Res.string.section_customer_contact),
                                    style = MaterialTheme.typography.titleMedium
                                )
                                Spacer(modifier = Modifier.height(Dimens.spacingMedium))
                                
                                InfoRow(
                                    label = "Host",
                                    value = event.customerName
                                )
                                InfoRow(
                                    label = stringResource(Res.string.label_email),
                                    value = event.customerEmail
                                )
                                InfoRow(
                                    label = stringResource(Res.string.label_phone),
                                    value = event.customerPhone
                                )
                                event.catererContact?.let { caterer ->
                                    if (caterer.isNotBlank()) {
                                        InfoRow(
                                            label = stringResource(Res.string.label_caterer),
                                            value = caterer
                                        )
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(Dimens.spacingLarge))

                        // Event Info Card
                        OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
                                Text(
                                    text = stringResource(Res.string.section_event_info),
                                    style = MaterialTheme.typography.titleMedium
                                )
                                Spacer(modifier = Modifier.height(Dimens.spacingMedium))

                                InfoRow(
                                    label = stringResource(Res.string.label_date_time),
                                    value = buildString {
                                        append(formatDate(event.eventDate))
                                        if (event.eventTime.isNotBlank()) append(", ${event.eventTime}")
                                        if (!event.eventEndTime.isNullOrBlank()) append(" – ${event.eventEndTime}")
                                    }
                                )
                                InfoRow(
                                    label = stringResource(Res.string.label_guests),
                                    value = stringResource(Res.string.label_guests_count, event.guestCount)
                                )
                                InfoRow(
                                    label = stringResource(Res.string.label_location),
                                    value = event.locationName
                                )
                                event.eventPurpose?.let { purpose ->
                                    if (purpose.isNotBlank()) {
                                        InfoRow(
                                            label = stringResource(Res.string.label_purpose),
                                            value = purpose
                                        )
                                    }
                                }
                                event.cost?.let { cost ->
                                    if (cost.isNotBlank()) InfoRow(
                                        label = stringResource(Res.string.label_cost),
                                        value = cost
                                    )
                                }
                                event.deposit?.let { deposit ->
                                    if (deposit.isNotBlank()) InfoRow(
                                        label = stringResource(Res.string.label_deposit),
                                        value = deposit
                                    )
                                }
                            }
                        }

                        // Notes
                        event.notes?.let { notes ->
                            if (notes.isNotBlank()) {
                                Spacer(modifier = Modifier.height(Dimens.spacingLarge))
                                OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                    Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
                                        Text(
                                            text = stringResource(Res.string.label_notes),
                                            style = MaterialTheme.typography.titleMedium
                                        )
                                        Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                                        Text(
                                            text = notes,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        }

                        // Post-Event Notes
                        event.postEventNotes?.let { notes ->
                            if (notes.isNotBlank()) {
                                Spacer(modifier = Modifier.height(Dimens.spacingLarge))
                                OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                    Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
                                        Text(
                                            text = stringResource(Res.string.label_post_event_notes),
                                            style = MaterialTheme.typography.titleMedium
                                        )
                                        Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                                        Text(
                                            text = notes,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(Dimens.spacingXLarge))

                        // Action buttons — row 1
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)
                        ) {
                            OutlinedButton(
                                onClick = { showStatusMenu = true },
                                modifier = Modifier.weight(1f)
                            ) {
                                Text(stringResource(Res.string.action_change_status))
                                DropdownMenu(
                                    expanded = showStatusMenu,
                                    onDismissRequest = { showStatusMenu = false }
                                ) {
                                    EventStatus.entries
                                        .filter { it != EventStatus.COMPLETED }
                                        .forEach { status ->
                                            DropdownMenuItem(
                                                text = { Text(status.name.lowercase().replaceFirstChar { it.uppercase() }) },
                                                onClick = {
                                                    pendingStatus = status
                                                    showStatusMenu = false
                                                }
                                            )
                                        }
                                }
                            }
                            Button(
                                onClick = { navigator.push(EventFormScreen(eventId = eventId)) },
                                modifier = Modifier.weight(1f)
                            ) {
                                Text(stringResource(Res.string.action_edit_event))
                            }
                        }

                        // Action buttons — row 2
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)
                        ) {
                            OutlinedButton(
                                onClick = { screenModel.sendToHost() },
                                modifier = Modifier.weight(1f),
                                enabled = !state.isSendingEmail
                            ) {
                                Text(if (state.isSendingEmail) "Sending..." else stringResource(Res.string.action_send_to_host))
                            }
                            OutlinedButton(
                                onClick = { screenModel.showPrintConfirmation() },
                                modifier = Modifier.weight(1f)
                            ) {
                                Text(stringResource(Res.string.action_print))
                            }
                        }
                    }
                }
            }
        }

        // Print confirmation dialog
        if (state.showPrintConfirm) {
            val event = state.event
            AlertDialog(
                onDismissRequest = screenModel::hidePrintConfirmation,
                title = { Text("Download Confirmation") },
                text = { Text("Generate and download the event confirmation PDF for ${event?.customerName ?: "this event"}?") },
                confirmButton = {
                    Button(
                        onClick = {
                            if (event != null) printEventConfirmation(event)
                            screenModel.hidePrintConfirmation()
                        }
                    ) {
                        Text("Download PDF")
                    }
                },
                dismissButton = {
                    TextButton(onClick = screenModel::hidePrintConfirmation) {
                        Text("Cancel")
                    }
                }
            )
        }

        // Missing email dialog
        if (state.showEmailMissing) {
            AlertDialog(
                onDismissRequest = screenModel::hideEmailMissing,
                title = { Text("Email Required") },
                text = {
                    Text(
                        "An email address is required to send the confirmation to the host. " +
                            "Please add an email to ${state.event?.customerName ?: "this host"} and try again."
                    )
                },
                confirmButton = {
                    Button(onClick = {
                        screenModel.hideEmailMissing()
                        navigator.push(EventFormScreen(eventId = eventId))
                    }) {
                        Text("Edit Event")
                    }
                },
                dismissButton = {
                    TextButton(onClick = screenModel::hideEmailMissing) {
                        Text("Close")
                    }
                }
            )
        }

        state.emailMessage?.let { message ->
            AlertDialog(
                onDismissRequest = screenModel::clearEmailMessage,
                title = { Text("Send to Host") },
                text = { Text(message) },
                confirmButton = {
                    Button(onClick = screenModel::clearEmailMessage) {
                        Text("OK")
                    }
                }
            )
        }

        // Email preview/edit dialog
        if (state.showEmailPreview) {
            val event = state.event
            AlertDialog(
                onDismissRequest = screenModel::dismissEmailPreview,
                title = { Text("Confirmation Preview") },
                text = {
                    Column {
                        Text(
                            text = "To: ${event?.customerEmail ?: ""}",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(Modifier.height(Dimens.spacingMedium))
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 400.dp)
                                .verticalScroll(rememberScrollState())
                        ) {
                            Text(
                                text = state.emailDraftBody.replace("%%LOGOIMG%%", "[Logo]"),
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = screenModel::confirmSendEmail,
                        enabled = !state.isSendingEmail
                    ) { Text(if (state.isSendingEmail) "Sending…" else "Send Email") }
                },
                dismissButton = {
                    TextButton(onClick = screenModel::dismissEmailPreview) { Text("Cancel") }
                }
            )
        }

        // Status change confirmation dialog
        pendingStatus?.let { newStatus ->
            val currentStatus = state.event?.status
            val fromLabel = currentStatus?.name?.lowercase()?.replaceFirstChar { it.uppercase() } ?: ""
            val toLabel = newStatus.name.lowercase().replaceFirstChar { it.uppercase() }
            ConfirmDialog(
                title = "Change Status",
                message = "Change event status from $fromLabel to $toLabel?",
                confirmText = "Yes, Change",
                dismissText = "Cancel",
                onConfirm = {
                    screenModel.updateStatus(newStatus)
                    @Suppress("UNUSED_VALUE")
                    pendingStatus = null
                },
                onDismiss = {
                    @Suppress("UNUSED_VALUE")
                    pendingStatus = null
                }
            )
        }

        // Delete confirmation dialog
        if (state.showDeleteConfirm) {
            ConfirmDialog(
                title = stringResource(Res.string.dialog_title_delete_event),
                message = stringResource(Res.string.msg_delete_confirm),
                confirmText = stringResource(Res.string.action_delete),
                isDestructive = true,
                onConfirm = screenModel::deleteEvent,
                onDismiss = screenModel::hideDeleteConfirmation
            )
        }
    }
}

@Composable
private fun InfoRow(
    label: String,
    value: String
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = Dimens.spacingSmall),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.width(110.dp)
            )
            Text(
                text = value,
                style = MaterialTheme.typography.bodyMedium,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

private fun formatDate(timestamp: Long): String {
    return try {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val localDateTime = instant.toLocalDateTime(TimeZone.UTC)
        val month = localDateTime.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }
        "$month ${localDateTime.day}, ${localDateTime.year}"
    } catch (_: Exception) {
        "Unknown date"
    }
}
